import React, { useState, useRef, useEffect } from "react";
import { Users, ChevronLeft, ChevronRight } from "lucide-react";

const CoursesSection = () => {
  const [activeTab, setActiveTab] = useState("age-6-9");
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const tabs = [
    { id: "age-6-9", label: "Age 6 to 9" },
    { id: "age-10-13", label: "Age 10 to 13" },
    { id: "age-14-18", label: "Age 14 to 18" }
  ];

  const courses = [
    {
      title: "AI & Robotics",
      description: "Build robots, train AI models and create smart technology projects while learning engineering and problem-solving skills.",
      ageGroup: "Ages 10–16",
      img: "/img/ai-lab/course_robotics.png",
      imgAlt: "Kids learning artificial intelligence and robotics classes at Practido Learning Center Panipat",
      btnClass: "purple",
      delay: "delay-100",
      categories: ["age-6-9", "age-10-13", "age-14-18"]
    },
    {
      title: "Coding & Programming",
      description: "Learn Scratch, Python and app development through interactive coding challenges and real projects.",
      ageGroup: "Ages 6–14",
      img: "/img/ai-lab/course_coding.png",
      imgAlt: "Children attending live coding classes for kids at Practido Learning Center",
      btnClass: "blue",
      delay: "delay-200",
      categories: ["age-6-9", "age-10-13", "age-14-18"]
    },
    {
      title: "Design & Creativity",
      description: "Develop creative thinking through graphic design, digital art, UI/UX and design thinking activities.",
      ageGroup: "Ages 8–14",
      img: "/img/ai-lab/course_design.png",
      imgAlt: "Students developing UI/UX and design thinking for kids through digital art projects",
      btnClass: "pink",
      delay: "delay-300",
      categories: ["age-6-9", "age-10-13", "age-14-18"]
    },
    {
      title: "3D Modeling & Printing",
      description: "Bring your ideas to life! Learn 3D modeling, computer-aided design (CAD) concepts, and how to configure and print physical objects using 3D printers.",
      ageGroup: "Ages 10–18",
      img: "/img/ai-lab/course_3dprint.png",
      imgAlt: "Students designing 3D models and printing physical objects on a 3D printer",
      btnClass: "orange",
      delay: "delay-100",
      categories: ["age-10-13", "age-14-18"]
    },
    {
      title: "Video Editing & Content Creation",
      description: "Learn storytelling, video production and digital content creation using modern tools.",
      ageGroup: "Ages 10–16",
      img: "/img/ai-lab/course_art.png",
      imgAlt: "Kids learning digital storytelling and video editing classes for kids at Practido",
      btnClass: "orange",
      delay: "delay-100",
      categories: ["age-6-9", "age-10-13", "age-14-18"]
    },
    {
      title: "Sketching and Drawing",
      description: "Learn digital art, sketching techniques, and illustration in a creative setting.",
      ageGroup: "Ages 6–14",
      img: "/img/ai-lab/course_sketching.png",
      imgAlt: "Kids learning sketching and digital art classes for kids at Practido",
      btnClass: "pink",
      delay: "delay-200",
      categories: ["age-6-9", "age-10-13", "age-14-18"]
    },
    {
      title: "Aerodynamics",
      description: "Understand the physics of flight through hands-on paper planes, drones, and engineering challenges.",
      ageGroup: "Ages 10–18",
      img: "/img/ai-lab/course_aerodynamics.png",
      imgAlt: "Students exploring aerodynamics and flight physics at Practido",
      btnClass: "blue",
      delay: "delay-300",
      categories: ["age-10-13", "age-14-18"]
    },
    {
      title: "STEM & Innovation",
      description: "Explore science, technology, engineering and mathematics through experiments and hands-on challenges.",
      ageGroup: "Ages 8–14",
      img: "/img/ai-lab/course_drones.png",
      imgAlt: "Students exploring STEM learning center science and engineering projects with drone tech",
      btnClass: "green",
      delay: "delay-200",
      categories: ["age-6-9", "age-10-13", "age-14-18"]
    },
    {
      title: "Smart Money Skills",
      description: "Financial literacy for the next generation — savings, entrepreneurship and money mindset, taught the fun way.",
      ageGroup: "Ages 8–14",
      img: "/img/ai-lab/course_money.png",
      imgAlt: "Kids practicing future skills classes for kids including financial literacy and savings mindset",
      btnClass: "cyan",
      delay: "delay-400",
      categories: ["age-6-9", "age-10-13", "age-14-18"]
    }
  ];

  const filteredCourses = courses.filter(course => course.categories.includes(activeTab));

  const updateArrowVisibility = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setShowLeftArrow(scrollLeft > 5);
      setShowRightArrow(scrollWidth - scrollLeft - clientWidth > 5);
    }
  };

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -392, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 392, behavior: "smooth" });
    }
  };

  // Reset scroll to start when tab changes
  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollLeft = 0;
      updateArrowVisibility();
    }
  }, [activeTab]);

  useEffect(() => {
    const updateIndicator = () => {
      if (containerRef.current) {
        const activeBtn = containerRef.current.querySelector(".courses-tab-btn.active");
        if (activeBtn) {
          setIndicatorStyle({
            left: activeBtn.offsetLeft,
            width: activeBtn.offsetWidth
          });
        }
      }
    };

    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    const timer = setTimeout(updateIndicator, 100);

    return () => {
      window.removeEventListener("resize", updateIndicator);
      clearTimeout(timer);
    };
  }, [activeTab]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      updateArrowVisibility();
      slider.addEventListener("scroll", updateArrowVisibility);
      window.addEventListener("resize", updateArrowVisibility);
    }
    return () => {
      if (slider) {
        slider.removeEventListener("scroll", updateArrowVisibility);
        window.removeEventListener("resize", updateArrowVisibility);
      }
    };
  }, [filteredCourses]);

  return (
    <section className="section courses section-shaded" id="courses">
      <div className="container">
        <div className="section-header reveal-triggered fade-up active" data-aos="fade-up">
          <h2>Future Skills Courses for <span className="text-gradient">Students</span></h2>
          <p>Comprehensive programs designed to build future-ready skills for students aged 6 to 18.</p>
        </div>

        <div className="courses-tabs-container" data-aos="fade-up" data-aos-delay="100">
          <div className="courses-tabs-list" ref={containerRef}>
            <div className="courses-active-indicator" style={indicatorStyle} />
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`courses-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="courses-slider-wrapper" data-aos="zoom-in" data-aos-delay="200">
          {showLeftArrow && (
            <button className="courses-scroll-btn left" onClick={scrollLeft} aria-label="Scroll left">
              <ChevronLeft size={24} />
            </button>
          )}

          <div className="courses-grid" ref={sliderRef}>
            {filteredCourses.map((course, index) => (
              <div
                key={`${course.title}-${activeTab}`}
                className={`premium-card course-card course-card-animate active`}
                data-aos="zoom-in"
                data-aos-delay={(index + 1) * 100}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="course-media">
                  <img src={course.img} alt={course.imgAlt} loading="lazy" />
                </div>
                <div className="course-info">
                  <h3>{course.title}</h3>
                  <p>{course.description}</p>
                  <a
                    href="https://wa.me/911204514794"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`course-btn ${course.btnClass}`}
                    style={{ display: "inline-block", textDecoration: "none" }}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            ))}
          </div>

          {showRightArrow && (
            <button className="courses-scroll-btn right" onClick={scrollRight} aria-label="Scroll right">
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default CoursesSection;
